/**
 * VSB Farm Management - Core Grower Settlement Calculation Engine
 * 
 * Complies strictly with VSB Implementation Plan (WP05), authoritative accounting
 * logic from VSB_SETTLEMENT_VALIDATION_CASES.md, and calculation-integrity guidelines:
 * 1. No automatic 50/50 fallback (explicit contract or historical evidence required).
 * 2. Strict feed cost segregation (no double-counting between bodega receipts & usage).
 * 3. No proxy FCR/ADG (computed strictly from actual physical data; null when incomplete).
 * 4. Authoritative booked financial facts (Pig Sales 1010/1011, eligible expenses 3050/3051).
 */

export interface CycleMetadata {
  id: string;
  cycle_code: string;
  batch_code: string;
  grower_id?: string | null;
  grower_name?: string | null;
  farm_id?: string | null;
  farm_name?: string | null;
  production_unit_id?: string | null;
  unit_code?: string | null;
  contract_id?: string | null;
  status: string;
  start_date: string;
  expected_sale_date?: string | null;
  actual_close_date?: string | null;
  legacy_raiser_name?: string | null;
  legacy_batch_code?: string | null;
}

export interface ContractProfitSplit {
  vsb_share_pct: number;
  grower_share_pct: number;
  source: 'grower_contract' | 'historical_evidence' | 'manual_override';
  contract_code?: string;
  notes?: string;
}

export interface PigArrivalItem {
  id: string;
  arrival_date: string;
  head_count: number;
  total_weight_kg?: number | null;
  base_amount: number;
  excess_weight_amount: number;
  trucking_cost: number;
  total_cost: number;
  notes?: string;
}

export interface MortalityEventItem {
  id: string;
  event_date: string;
  head_count: number;
  total_weight_kg?: number | null;
  cause_of_death?: string | null;
}

export interface FeedReceiptItem {
  id: string;
  receipt_date: string;
  quantity_bags: number;
  total_weight_kg?: number | null;
  unit_price: number;
  feed_cost: number;
  feed_markup_amount?: number | null;
  trucking_cost?: number | null;
  total_cost: number;
  is_direct_to_cycle: boolean;
  cycle_id?: string | null;
}

export interface FeedUsageItem {
  id: string;
  receipt_id?: string | null;
  cycle_id: string;
  usage_date: string;
  quantity_bags: number;
  total_weight_kg?: number | null;
  unit_cost: number;
  total_cost: number;
}

export interface FeedTransferItem {
  id: string;
  transfer_date: string;
  source_cycle_id: string;
  destination_cycle_id: string;
  quantity_bags: number;
  total_weight_kg?: number | null;
  unit_price: number;
  total_transferred_cost: number;
  notes?: string | null;
}

export interface MedicineReceiptItem {
  id: string;
  receipt_date: string;
  quantity: number;
  unit_price: number;
  total_amount: number;
  account_code?: string | null;
  category_id?: string | null;
  description?: string;
}

export interface HealthEventItem {
  id: string;
  event_date: string;
  event_type: string;
  head_count_treated?: number | null;
  total_cost: number;
  diagnostic_result?: string | null;
}

export interface ExpenseItem {
  id: string;
  expense_date: string;
  amount: number;
  account_code?: string | null;
  category_id?: string | null;
  description: string;
  is_settlement_eligible?: boolean;
}

export interface PigSaleItem {
  id: string;
  sale_date: string;
  head_count: number;
  gross_weight_kg: number;
  shrinkage_rate_applied?: number | null;
  shrinkage_deduction_kg?: number | null;
  net_weight_kg: number;
  price_per_kg: number;
  total_amount: number;
  scale_ticket_no?: string | null;
  account_code?: string | null;
}

export interface SettlementAdjustmentItem {
  id: string;
  adjustment_date: string;
  account_code: string; // '4010', '4020', '4030', '4031', '4040', '4041', '4050'
  adjustment_type: string;
  amount: number;
  quantity?: number | null;
  unit_rate?: number | null;
  description: string;
  rate_rule_id?: string | null;
}

export interface ReconciliationIssueItem {
  id: string;
  issue_type: string;
  severity: string;
  status: string;
  description: string;
}

export interface SettlementCalculationInput {
  cycle: CycleMetadata;
  contractSplit?: ContractProfitSplit | null;
  pigArrivals: PigArrivalItem[];
  mortalityEvents: MortalityEventItem[];
  feedReceipts: FeedReceiptItem[];
  feedUsage: FeedUsageItem[];
  feedTransfers?: FeedTransferItem[];
  medicineReceipts: MedicineReceiptItem[];
  healthEvents: HealthEventItem[];
  expenses: ExpenseItem[];
  pigSales: PigSaleItem[];
  adjustments: SettlementAdjustmentItem[];
  reconciliationIssues?: ReconciliationIssueItem[];
}

export interface SettlementLineItem {
  line_number: number;
  line_type: 'sales_revenue' | 'piglet_cost' | 'feed_cost' | 'med_cost' | 'expense' | 'adjustment_deduction' | 'adjustment_addition';
  account_code: string;
  description: string;
  quantity?: number | null;
  unit_measurement?: string | null;
  unit_price?: number | null;
  total_amount: number;
  is_deduction: boolean;
  reference_table?: string;
  reference_id?: string;
}

export interface PhysicalKPIResult {
  mortality_rate_pct: number | null;
  mortality_note?: string;
  fcr: number | null;
  fcr_note?: string;
  adg_kg_day: number | null;
  adg_note?: string;
  total_placed_heads: number;
  total_sold_heads: number;
  total_mortality_heads: number;
  net_head_balance: number;
  total_placed_weight_kg: number | null;
  total_sold_net_weight_kg: number;
  total_feed_consumed_kg: number | null;
  days_on_feed: number | null;
}

export interface SettlementCalculationResult {
  success: boolean;
  error?: string;
  validation_errors?: string[];
  warnings: string[];
  
  // Cycle & Identity
  cycle_id: string;
  cycle_code: string;
  batch_code: string;
  grower_name: string;
  
  // Profit Split
  profit_split: ContractProfitSplit;
  
  // Revenue & Production Costs
  total_sales_revenue: number;
  total_piglet_cost: number;
  total_feed_cost: number;
  total_med_cost: number;
  total_other_costs: number;
  total_production_expenses: number;
  
  // Profitability
  gross_profit: number; // Net Operating Income
  vsb_share_amount: number;
  grower_gross_share: number;
  
  // Post-Profit Adjustments
  total_adjustments_deductions: number;
  total_adjustments_additions: number;
  net_adjustments: number; // Deductions - Additions
  
  // Final Net Payout
  grower_net_payout: number;
  
  // Breakdown & Physical KPIs
  settlement_lines: SettlementLineItem[];
  kpis: PhysicalKPIResult;
  
  // Snapshot data object for settlements.snapshot_data
  snapshot_data: Record<string, any>;
}

/**
 * Rounds a number to exactly two decimal places (centavo standard) using decimal exponential rounding.
 */
export function roundToCentavos(num: number): number {
  if (isNaN(num)) return 0;
  return Number(Math.round(Number(num + 'e2')) + 'e-2');
}

/**
 * Calculates a complete Grower Settlement liquidation from operational records.
 */
export function calculateSettlement(input: SettlementCalculationInput): SettlementCalculationResult {
  const warnings: string[] = [];
  const validationErrors: string[] = [];
  
  // --------------------------------------------------------------------------
  // 1. Profit Split Verification (Strict: No Silent 50/50 Fallback)
  // --------------------------------------------------------------------------
  if (!input.contractSplit) {
    validationErrors.push(
      `No valid profit split contract or historical evidence established for cycle ${input.cycle.cycle_code} (${input.cycle.legacy_raiser_name || 'Unknown Raiser'}). Automatic 50/50 fallback is prohibited.`
    );
  } else {
    const totalSplit = input.contractSplit.vsb_share_pct + input.contractSplit.grower_share_pct;
    if (Math.abs(totalSplit - 100) > 0.01) {
      validationErrors.push(
        `Invalid contract profit split for cycle ${input.cycle.cycle_code}: VSB (${input.contractSplit.vsb_share_pct}%) + Grower (${input.contractSplit.grower_share_pct}%) must equal 100%.`
      );
    }
  }

  if (validationErrors.length > 0) {
    return {
      success: false,
      validation_errors: validationErrors,
      warnings,
      cycle_id: input.cycle.id,
      cycle_code: input.cycle.cycle_code,
      batch_code: input.cycle.batch_code,
      grower_name: input.cycle.grower_name || input.cycle.legacy_raiser_name || 'Unknown',
      profit_split: input.contractSplit || { vsb_share_pct: 0, grower_share_pct: 0, source: 'grower_contract' },
      total_sales_revenue: 0,
      total_piglet_cost: 0,
      total_feed_cost: 0,
      total_med_cost: 0,
      total_other_costs: 0,
      total_production_expenses: 0,
      gross_profit: 0,
      vsb_share_amount: 0,
      grower_gross_share: 0,
      total_adjustments_deductions: 0,
      total_adjustments_additions: 0,
      net_adjustments: 0,
      grower_net_payout: 0,
      settlement_lines: [],
      kpis: {
        mortality_rate_pct: null,
        fcr: null,
        adg_kg_day: null,
        total_placed_heads: 0,
        total_sold_heads: 0,
        total_mortality_heads: 0,
        net_head_balance: 0,
        total_placed_weight_kg: null,
        total_sold_net_weight_kg: 0,
        total_feed_consumed_kg: null,
        days_on_feed: null,
      },
      snapshot_data: {},
    };
  }

  const profitSplit = input.contractSplit!;
  const lines: SettlementLineItem[] = [];
  let lineNum = 1;

  // --------------------------------------------------------------------------
  // 2. Pig Sales Revenue (Authoritative Booked Total from Accounts 1010/1011)
  // --------------------------------------------------------------------------
  let totalSalesRevenue = 0;
  let totalSoldHeads = 0;
  let totalSoldNetKg = 0;
  let totalGrossKg = 0;
  let totalShrinkKg = 0;

  for (const sale of input.pigSales) {
    const saleAmt = roundToCentavos(sale.total_amount);
    totalSalesRevenue += saleAmt;
    totalSoldHeads += sale.head_count || 0;
    totalSoldNetKg += sale.net_weight_kg || 0;
    totalGrossKg += sale.gross_weight_kg || 0;
    totalShrinkKg += sale.shrinkage_deduction_kg || 0;

    lines.push({
      line_number: lineNum++,
      line_type: 'sales_revenue',
      account_code: sale.account_code || '1010',
      description: `Pig Sale ${sale.scale_ticket_no ? `(Ticket #${sale.scale_ticket_no})` : ''} - ${sale.head_count} head, ${sale.net_weight_kg} kg @ ₱${sale.price_per_kg}/kg`,
      quantity: sale.head_count,
      unit_measurement: 'heads',
      unit_price: sale.price_per_kg,
      total_amount: saleAmt,
      is_deduction: false,
      reference_table: 'pig_sales',
      reference_id: sale.id,
    });
  }
  totalSalesRevenue = roundToCentavos(totalSalesRevenue);

  // --------------------------------------------------------------------------
  // 3. Piglet Intake Costs (Accounts 3010, 3012, 3015)
  // --------------------------------------------------------------------------
  let totalPigletCost = 0;
  let totalPlacedHeads = 0;
  let totalPlacedWeightKg: number | null = null;
  let hasPlacedWeight = false;

  for (const arrival of input.pigArrivals) {
    const arrivalCost = roundToCentavos(arrival.total_cost);
    totalPigletCost += arrivalCost;
    totalPlacedHeads += arrival.head_count || 0;

    if (arrival.total_weight_kg !== null && arrival.total_weight_kg !== undefined && arrival.total_weight_kg > 0) {
      totalPlacedWeightKg = (totalPlacedWeightKg || 0) + arrival.total_weight_kg;
      hasPlacedWeight = true;
    }

    lines.push({
      line_number: lineNum++,
      line_type: 'piglet_cost',
      account_code: '3010',
      description: `Piglet Arrival - ${arrival.head_count} head (Base: ₱${roundToCentavos(arrival.base_amount)}, Trucking: ₱${roundToCentavos(arrival.trucking_cost)}, Excess: ₱${roundToCentavos(arrival.excess_weight_amount)})`,
      quantity: arrival.head_count,
      unit_measurement: 'heads',
      unit_price: arrival.head_count > 0 ? roundToCentavos(arrival.base_amount / arrival.head_count) : 0,
      total_amount: arrivalCost,
      is_deduction: true,
      reference_table: 'pig_arrivals',
      reference_id: arrival.id,
    });
  }
  totalPigletCost = roundToCentavos(totalPigletCost);

  // --------------------------------------------------------------------------
  // 4. Feed Costs & Segregation (Prevent Bodega Double-Counting)
  // --------------------------------------------------------------------------
  let totalFeedCost = 0;
  let totalFeedBags = 0;
  let totalFeedKg: number | null = null;
  let hasFeedKg = false;

  // Track bodega receipt IDs that are allocated via feed_usage to prevent double-counting
  const allocatedReceiptIds = new Set<string>();
  for (const usage of input.feedUsage) {
    if (usage.receipt_id) allocatedReceiptIds.add(usage.receipt_id);
  }

  // A. Direct Cycle Deliveries (feed_receipts directly for cycle or historical imported cycle records)
  for (const rcpt of input.feedReceipts) {
    // If a feed receipt is specifically a bodega purchase AND is subsequently distributed via feed_usage,
    // do NOT charge the bodega receipt directly to the cycle to prevent double-counting.
    if (!rcpt.is_direct_to_cycle && allocatedReceiptIds.has(rcpt.id)) {
      continue;
    }

    const cost = roundToCentavos(rcpt.total_cost);
    totalFeedCost += cost;
    if (rcpt.feed_cost > 0 || (rcpt.unit_price !== undefined && rcpt.unit_price > 0)) {
      totalFeedBags += rcpt.quantity_bags || 0;
    }
    if (rcpt.total_weight_kg !== null && rcpt.total_weight_kg !== undefined && rcpt.total_weight_kg > 0) {
      totalFeedKg = (totalFeedKg || 0) + rcpt.total_weight_kg;
      hasFeedKg = true;
    }

    lines.push({
      line_number: lineNum++,
      line_type: 'feed_cost',
      account_code: '3020',
      description: `Feed Delivery - ${rcpt.quantity_bags} bags @ ₱${rcpt.unit_price}/bag`,
      quantity: rcpt.quantity_bags,
      unit_measurement: 'bags',
      unit_price: rcpt.unit_price,
      total_amount: cost,
      is_deduction: true,
      reference_table: 'feed_receipts',
      reference_id: rcpt.id,
    });
  }

  // B. Bodega Usage Allocations (feed_usage from farm storage - legacy)
  for (const usage of input.feedUsage) {
    const cost = roundToCentavos(usage.total_cost);
    totalFeedCost += cost;
    totalFeedBags += usage.quantity_bags || 0;
    if (usage.total_weight_kg !== null && usage.total_weight_kg !== undefined && usage.total_weight_kg > 0) {
      totalFeedKg = (totalFeedKg || 0) + usage.total_weight_kg;
      hasFeedKg = true;
    }

    lines.push({
      line_number: lineNum++,
      line_type: 'feed_cost',
      account_code: '3020',
      description: `Feed Bodega Allocation - ${usage.quantity_bags} bags @ ₱${usage.unit_cost}/bag`,
      quantity: usage.quantity_bags,
      unit_measurement: 'bags',
      unit_price: usage.unit_cost,
      total_amount: cost,
      is_deduction: true,
      reference_table: 'feed_usage',
      reference_id: usage.id,
    });
  }

  // C. Cycle-to-Cycle Feed Transfers (Inbound Debits & Outbound Credits)
  if (input.feedTransfers && input.feedTransfers.length > 0) {
    for (const transfer of input.feedTransfers) {
      const isDestination = transfer.destination_cycle_id === input.cycle.id;
      const isSource = transfer.source_cycle_id === input.cycle.id;
      const cost = roundToCentavos(transfer.total_transferred_cost);

      if (isDestination) {
        // Inbound Feed Transfer: Cycle is debited with feed cost
        totalFeedCost += cost;
        totalFeedBags += transfer.quantity_bags || 0;
        if (transfer.total_weight_kg !== null && transfer.total_weight_kg !== undefined && transfer.total_weight_kg > 0) {
          totalFeedKg = (totalFeedKg || 0) + transfer.total_weight_kg;
          hasFeedKg = true;
        }

        lines.push({
          line_number: lineNum++,
          line_type: 'feed_cost',
          account_code: '3020',
          description: `Feed Transfer In - ${transfer.quantity_bags} bags @ ₱${Number(transfer.unit_price).toFixed(2)}/bag`,
          quantity: transfer.quantity_bags,
          unit_measurement: 'bags',
          unit_price: transfer.unit_price,
          total_amount: cost,
          is_deduction: true,
          reference_table: 'feed_transfers',
          reference_id: transfer.id,
        });
      } else if (isSource) {
        // Outbound Feed Transfer: Cycle is credited (feed cost deducted)
        totalFeedCost -= cost;
        totalFeedBags -= transfer.quantity_bags || 0;
        if (transfer.total_weight_kg !== null && transfer.total_weight_kg !== undefined && transfer.total_weight_kg > 0) {
          totalFeedKg = (totalFeedKg || 0) - transfer.total_weight_kg;
        }

        lines.push({
          line_number: lineNum++,
          line_type: 'feed_cost',
          account_code: '3020',
          description: `Feed Transfer Out Credit - ${transfer.quantity_bags} bags @ ₱${Number(transfer.unit_price).toFixed(2)}/bag`,
          quantity: -transfer.quantity_bags,
          unit_measurement: 'bags',
          unit_price: transfer.unit_price,
          total_amount: -cost,
          is_deduction: true,
          reference_table: 'feed_transfers',
          reference_id: transfer.id,
        });
      }
    }
  }

  totalFeedCost = roundToCentavos(totalFeedCost);

  // --------------------------------------------------------------------------
  // 5. Veterinary Medicines & Vaccines (Accounts 3030, 3031, 3032, 3034)
  // --------------------------------------------------------------------------
  let totalMedCost = 0;

  for (const med of input.medicineReceipts) {
    const cost = roundToCentavos(med.total_amount);
    totalMedCost += cost;

    lines.push({
      line_number: lineNum++,
      line_type: 'med_cost',
      account_code: med.account_code || '3030',
      description: `Veterinary Medicine - ${med.description || 'Medication'} (${med.quantity} units @ ₱${med.unit_price})`,
      quantity: med.quantity,
      unit_measurement: 'units',
      unit_price: med.unit_price,
      total_amount: cost,
      is_deduction: true,
      reference_table: 'medicine_receipts',
      reference_id: med.id,
    });
  }

  // Only include health events if they are not already captured via medicine receipts
  if (input.medicineReceipts.length === 0) {
    for (const health of input.healthEvents) {
      const cost = roundToCentavos(health.total_cost);
      if (cost > 0) {
        totalMedCost += cost;

        lines.push({
          line_number: lineNum++,
          line_type: 'med_cost',
          account_code: '3034',
          description: `Health Event - ${health.event_type} (${health.head_count_treated || 0} head treated)`,
          quantity: health.head_count_treated,
          unit_measurement: 'heads',
          unit_price: health.head_count_treated && health.head_count_treated > 0 ? roundToCentavos(cost / health.head_count_treated) : 0,
          total_amount: cost,
          is_deduction: true,
          reference_table: 'health_events',
          reference_id: health.id,
        });
      }
    }
  }
  totalMedCost = roundToCentavos(totalMedCost);

  // --------------------------------------------------------------------------
  // 6. Settlement-Eligible Expenses (Accounts 3012 Trucking, 3015 Excess Kg, 3050 Management, 3051 Permits)
  // --------------------------------------------------------------------------
  let totalOtherCosts = 0;

  for (const exp of input.expenses) {
    // Check if expense is settlement-eligible:
    // 1. Explicit flag on expense record takes precedence.
    // 2. Default fallback covers established expense-table accounts (3012 Piglet Trucking, 3015 Excess Weight, 3050 Management, 3051 Permits, 3060 Other Expenses)
    //    while preventing double-counting with dedicated tables (3010 Arrivals, 3020 Feeds, 3030-3034 Meds).
    const acct = (exp.account_code || '').trim();
    const isEligible = exp.is_settlement_eligible !== undefined 
      ? exp.is_settlement_eligible 
      : (acct === '3012' || acct === '3015' || acct === '3040' || acct === '3050' || acct === '3051' || acct.startsWith('304') || acct.startsWith('305') || acct.startsWith('306'));

    if (isEligible) {
      const cost = roundToCentavos(exp.amount);

      if (acct === '3012' || acct === '3015') {
        totalPigletCost += cost;
        lines.push({
          line_number: lineNum++,
          line_type: 'piglet_cost',
          account_code: acct,
          description: `Piglet Cost (${acct === '3012' ? 'Trucking' : 'Excess Weight'}) - ${exp.description}`,
          total_amount: cost,
          is_deduction: true,
          reference_table: 'expenses',
          reference_id: exp.id,
        });
      } else if (acct === '3040') {
        totalOtherCosts += cost;
        lines.push({
          line_number: lineNum++,
          line_type: 'expense',
          account_code: '3040',
          description: `Feed Trucking Expense - ${exp.description || 'Trucking - Feeds'}`,
          total_amount: cost,
          is_deduction: true,
          reference_table: 'expenses',
          reference_id: exp.id,
        });
      } else {
        totalOtherCosts += cost;
        lines.push({
          line_number: lineNum++,
          line_type: 'expense',
          account_code: exp.account_code || '3050',
          description: `Operational Expense - ${exp.description}`,
          total_amount: cost,
          is_deduction: true,
          reference_table: 'expenses',
          reference_id: exp.id,
        });
      }
    } else {
      warnings.push(`Cycle expense ₱${exp.amount} (${exp.description}) was excluded from grower settlement deductions as it is marked non-settlement eligible.`);
    }
  }
  totalPigletCost = roundToCentavos(totalPigletCost);
  totalOtherCosts = roundToCentavos(totalOtherCosts);

  // --------------------------------------------------------------------------
  // 7. Net Operating Income & Contract Profit Split
  // --------------------------------------------------------------------------
  const totalProductionExpenses = roundToCentavos(
    totalPigletCost + totalFeedCost + totalMedCost + totalOtherCosts
  );
  const grossProfit = roundToCentavos(totalSalesRevenue - totalProductionExpenses);

  const growerGrossShare = roundToCentavos(grossProfit * (profitSplit.grower_share_pct / 100));
  const vsbShareAmount = roundToCentavos(grossProfit * (profitSplit.vsb_share_pct / 100));

  // --------------------------------------------------------------------------
  // 8. Post-Profit Adjustments (Accounts 4010–4050)
  // --------------------------------------------------------------------------
  let totalAdjustmentsDeductions = 0;
  let totalAdjustmentsAdditions = 0;

  for (const adj of input.adjustments) {
    const amt = roundToCentavos(adj.amount);
    const acct = (adj.account_code || '').trim();

    if (acct === '4050') {
      // Account 4050: Raiser's Expense Reimbursement (addition / credit to grower)
      totalAdjustmentsAdditions += amt;
      lines.push({
        line_number: lineNum++,
        line_type: 'adjustment_addition',
        account_code: acct,
        description: `Add: Raiser Expense Reimbursement - ${adj.description}`,
        quantity: adj.quantity,
        unit_price: adj.unit_rate,
        total_amount: amt,
        is_deduction: false,
        reference_table: 'settlement_adjustments',
        reference_id: adj.id,
      });
    } else {
      // Accounts 4010, 4020, 4030, 4031, 4040, 4041: Deductions from grower
      totalAdjustmentsDeductions += amt;
      lines.push({
        line_number: lineNum++,
        line_type: 'adjustment_deduction',
        account_code: acct,
        description: `Less: [${acct}] ${adj.adjustment_type} - ${adj.description}`,
        quantity: adj.quantity,
        unit_price: adj.unit_rate,
        total_amount: amt,
        is_deduction: true,
        reference_table: 'settlement_adjustments',
        reference_id: adj.id,
      });
    }
  }

  totalAdjustmentsDeductions = roundToCentavos(totalAdjustmentsDeductions);
  totalAdjustmentsAdditions = roundToCentavos(totalAdjustmentsAdditions);
  const netAdjustments = roundToCentavos(totalAdjustmentsDeductions - totalAdjustmentsAdditions);

  // Final Net Payout to Grower
  const growerNetPayout = roundToCentavos(growerGrossShare - netAdjustments);

  // --------------------------------------------------------------------------
  // 9. Physical KPIs Calculation (Strict: No Proxy Calculations)
  // --------------------------------------------------------------------------
  const totalMortalityHeads = input.mortalityEvents.reduce((acc, m) => acc + (m.head_count || 0), 0);
  const netHeadBalance = totalPlacedHeads - totalSoldHeads - totalMortalityHeads;

  // Physical Mortality %
  let mortalityRatePct: number | null = null;
  let mortalityNote: string | undefined;
  if (totalPlacedHeads > 0) {
    mortalityRatePct = roundToCentavos((totalMortalityHeads / totalPlacedHeads) * 100);
  } else {
    mortalityRatePct = null;
    mortalityNote = 'Initial placed headcount is 0 or unrecorded; mortality % cannot be computed without physical intake.';
    if (totalSoldHeads > 0) {
      warnings.push(`Anomaly: Cycle has 0 recorded placed piglets but ${totalSoldHeads} pigs sold. Physical intake data is missing.`);
    }
  }

  // Physical FCR
  let fcr: number | null = null;
  let fcrNote: string | undefined;
  if (hasPlacedWeight && totalPlacedWeightKg !== null && totalPlacedWeightKg > 0 && totalSoldNetKg > totalPlacedWeightKg && hasFeedKg && totalFeedKg !== null && totalFeedKg > 0) {
    const netWeightGainKg = totalSoldNetKg - totalPlacedWeightKg;
    fcr = Number(Math.round(Number((totalFeedKg / netWeightGainKg) + 'e4')) + 'e-4');
  } else {
    fcr = null;
    fcrNote = 'Physical FCR unavailable: Requires both measured piglet intake weight and physical feed weight (kg). Proxy estimation is prohibited.';
  }

  // Physical ADG
  let adgKgDay: number | null = null;
  let adgNote: string | undefined;
  let daysOnFeed: number | null = null;

  if (input.cycle.start_date && (input.cycle.actual_close_date || input.cycle.expected_sale_date || input.pigSales[0]?.sale_date)) {
    const startDate = new Date(input.cycle.start_date);
    const endDateStr = input.cycle.actual_close_date || input.pigSales[input.pigSales.length - 1]?.sale_date || input.cycle.expected_sale_date;
    if (endDateStr) {
      const endDate = new Date(endDateStr);
      const diffMs = endDate.getTime() - startDate.getTime();
      if (diffMs > 0) {
        daysOnFeed = Math.round(diffMs / (1000 * 60 * 60 * 24));
      }
    }
  }

  if (daysOnFeed !== null && daysOnFeed > 0 && totalSoldHeads > 0 && hasPlacedWeight && totalPlacedWeightKg !== null && totalPlacedWeightKg > 0 && totalSoldNetKg > totalPlacedWeightKg) {
    const netWeightGainKg = totalSoldNetKg - totalPlacedWeightKg;
    const gainPerHead = netWeightGainKg / totalSoldHeads;
    adgKgDay = Number(Math.round(Number((gainPerHead / daysOnFeed) + 'e4')) + 'e-4');
  } else {
    adgKgDay = null;
    adgNote = 'Physical ADG unavailable: Requires measured weight gain, sold headcount, and verified days on feed. Proxy estimation is prohibited.';
  }

  // Additional Physical Anomaly Warnings
  if (totalSalesRevenue === 0 && totalProductionExpenses > 0) {
    warnings.push(`Cycle has ₱${totalProductionExpenses.toFixed(2)} in expenses but ₱0.00 in sales revenue (Cycle in progress or holding/feed expense only).`);
  }
  if (growerNetPayout < 0) {
    warnings.push(`Negative grower net payout (₱${growerNetPayout.toFixed(2)}): Adjustments or net losses exceed grower share; balance is payable by raiser.`);
  }

  // --------------------------------------------------------------------------
  // 10. Construct Immutable Snapshot Data Object
  // --------------------------------------------------------------------------
  const snapshot_data = {
    settlement_engine_version: '1.0.0-WP05',
    calculated_at: new Date().toISOString(),
    cycle: {
      id: input.cycle.id,
      cycle_code: input.cycle.cycle_code,
      batch_code: input.cycle.batch_code,
      grower_name: input.cycle.grower_name || input.cycle.legacy_raiser_name,
      farm_name: input.cycle.farm_name,
      unit_code: input.cycle.unit_code,
      start_date: input.cycle.start_date,
      end_date: input.cycle.actual_close_date || input.cycle.expected_sale_date,
    },
    profit_split: profitSplit,
    financial_summary: {
      total_sales_revenue: totalSalesRevenue,
      total_piglet_cost: totalPigletCost,
      total_feed_cost: totalFeedCost,
      total_med_cost: totalMedCost,
      total_other_costs: totalOtherCosts,
      total_production_expenses: totalProductionExpenses,
      gross_profit: grossProfit,
      vsb_share_pct: profitSplit.vsb_share_pct,
      vsb_share_amount: vsbShareAmount,
      grower_share_pct: profitSplit.grower_share_pct,
      grower_gross_share: growerGrossShare,
      total_adjustments_deductions: totalAdjustmentsDeductions,
      total_adjustments_additions: totalAdjustmentsAdditions,
      net_adjustments: netAdjustments,
      grower_net_payout: growerNetPayout,
    },
    physical_summary: {
      total_placed_heads: totalPlacedHeads,
      total_sold_heads: totalSoldHeads,
      total_mortality_heads: totalMortalityHeads,
      net_head_balance: netHeadBalance,
      total_placed_weight_kg: totalPlacedWeightKg,
      total_sold_net_weight_kg: totalSoldNetKg,
      total_sold_gross_weight_kg: totalGrossKg,
      total_shrinkage_deduction_kg: totalShrinkKg,
      total_feed_bags: totalFeedBags,
      total_feed_weight_kg: totalFeedKg,
      days_on_feed: daysOnFeed,
      mortality_rate_pct: mortalityRatePct,
      fcr: fcr,
      adg_kg_day: adgKgDay,
      kpi_notes: {
        mortality: mortalityNote,
        fcr: fcrNote,
        adg: adgNote,
      },
    },
    itemized_lines: lines,
    warnings,
  };

  return {
    success: true,
    warnings,
    cycle_id: input.cycle.id,
    cycle_code: input.cycle.cycle_code,
    batch_code: input.cycle.batch_code,
    grower_name: input.cycle.grower_name || input.cycle.legacy_raiser_name || 'Unknown',
    profit_split: profitSplit,
    total_sales_revenue: totalSalesRevenue,
    total_piglet_cost: totalPigletCost,
    total_feed_cost: totalFeedCost,
    total_med_cost: totalMedCost,
    total_other_costs: totalOtherCosts,
    total_production_expenses: totalProductionExpenses,
    gross_profit: grossProfit,
    vsb_share_amount: vsbShareAmount,
    grower_gross_share: growerGrossShare,
    total_adjustments_deductions: totalAdjustmentsDeductions,
    total_adjustments_additions: totalAdjustmentsAdditions,
    net_adjustments: netAdjustments,
    grower_net_payout: growerNetPayout,
    settlement_lines: lines,
    kpis: {
      mortality_rate_pct: mortalityRatePct,
      mortality_note: mortalityNote,
      fcr,
      fcr_note: fcrNote,
      adg_kg_day: adgKgDay,
      adg_note: adgNote,
      total_placed_heads: totalPlacedHeads,
      total_sold_heads: totalSoldHeads,
      total_mortality_heads: totalMortalityHeads,
      net_head_balance: netHeadBalance,
      total_placed_weight_kg: totalPlacedWeightKg,
      total_sold_net_weight_kg: totalSoldNetKg,
      total_feed_consumed_kg: totalFeedKg,
      days_on_feed: daysOnFeed,
    },
    snapshot_data,
  };
}
